import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { AvatarPicker } from "@/components/Avatar";
import { uploadPhoto } from "@/components/photoUpload";
import { errText } from "@/components/ContentBits";

/** Edit your own avatar, bio and pinned list in place on your profile page. onSaved gets the saved profile text. */
export default function ProfileEditor({ onSaved, onCancel }) {
  const [about, setAbout] = useState(null);
  const [lists, setLists] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pic, setPic] = useState(false);
  const uploadAvatar = async (file) => {
    setPic(true);
    try {
      const v = await uploadPhoto(file, "avatar");
      setAbout((a) => ({ ...a, avatar: "", avatar_media_id: v.id, avatar_url: v.url }));
    } catch (err) { toast.error(errText(err, "Could not upload that photo. Please try another.")); }
    finally { setPic(false); }
  };
  useEffect(() => {
    api.get("/about").then((r) => setAbout(r.data)).catch(() => setAbout({ bio: "", fav_show: "", first_show: "", avatar: "", pinned_list_id: "" }));
    api.get("/lists").then((r) => setLists(r.data.filter((l) => l.visibility !== "private"))).catch(() => setLists([]));
  }, []);
  if (!about) return <p className="mt-4 text-sm text-muted-foreground">Loading…</p>;
  const set = (k) => (e) => setAbout({ ...about, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.put("/about", { bio: about.bio, fav_show: about.fav_show, first_show: about.first_show, avatar: about.avatar || "", avatar_media_id: about.avatar_media_id || "", pinned_list_id: about.pinned_list_id || null });
      toast.success("Profile saved");
      onSaved(r.data);
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not save that. Please try again.");
    } finally { setBusy(false); }
  };
  const input = "w-full rounded-lg border border-border bg-background px-3 py-2 text-base";
  return (
    <form onSubmit={save} className="mt-4 space-y-4 rounded-xl border border-border bg-background/60 p-4" data-testid="profile-editor">
      <div>
        <p className="mb-1 text-sm font-medium">Avatar</p>
        <AvatarPicker value={about.avatar || ""} photoUrl={about.avatar_url || ""} uploading={pic} onPhoto={uploadAvatar}
            onRemovePhoto={() => setAbout({ ...about, avatar_media_id: "", avatar_url: "" })}
            onChange={(a) => setAbout({ ...about, avatar: a, avatar_media_id: "", avatar_url: "" })} />
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium" htmlFor="pe-bio">Bio <span className="font-normal text-muted-foreground">({about.bio.length} / 160)</span></label>
        <input id="pe-bio" data-testid="pe-bio" maxLength={160} value={about.bio} onChange={set("bio")} placeholder="e.g. Musical theatre addict, usually in the cheap seats" className={input} />
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="mb-1 block text-sm font-medium" htmlFor="pe-fav">Favourite show</label>
          <input id="pe-fav" data-testid="pe-fav" maxLength={80} value={about.fav_show} onChange={set("fav_show")} className={input} />
        </div>
        <div>
          <label className="mb-1 block text-sm font-medium" htmlFor="pe-first">First show I saw</label>
          <input id="pe-first" data-testid="pe-first" maxLength={80} value={about.first_show} onChange={set("first_show")} className={input} />
        </div>
      </div>
      <div>
        <label className="mb-1 block text-sm font-medium" htmlFor="pe-pin">Pinned list</label>
        <select id="pe-pin" data-testid="pe-pin" value={about.pinned_list_id || ""} onChange={set("pinned_list_id")} className={input}>
          <option value="">None</option>
          {lists.map((l) => <option key={l.id} value={l.id}>{l.title}</option>)}
        </select>
        {lists.length === 0 && <p className="mt-1 text-xs text-muted-foreground">Share a list with your friends (Lists tab) to be able to pin it.</p>}
      </div>
      <p className="text-xs text-muted-foreground">Your profile details are shown only to you and your accepted friends.</p>
      <div className="flex items-center gap-4">
        <button type="submit" disabled={busy} data-testid="pe-save" className="rounded-full bg-[#C89B3C] px-6 py-2.5 text-sm font-semibold text-black disabled:opacity-60">Save profile</button>
        <button type="button" onClick={onCancel} data-testid="pe-cancel" className="text-sm text-muted-foreground underline">Cancel</button>
      </div>
    </form>
  );
}